import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';
import { showToast } from '@/utils/globalToast';
import { copyToClipboard } from './jobHelpers';

/** Flutter AppConstants.copyToClipboard (shown with AppConstants.showToast → Toast type "info"). */
export const COPIED_TO_CLIPBOARD = 'Copied to your clipboard !';

/** Flutter's long-press duration (kLongPressTimeout). */
const LONG_PRESS_MS = 500;

/**
 * Long-press to copy — mirrors the Flutter `onLongPress: Clipboard.setData(text) → showToast`
 * on job titles and Job IDs (WebJobDetailsCard, SimiliarJobCard, JobDetailsView hero).
 * Spread the returned handlers on the element. The click that ends a long press is swallowed,
 * so it doesn't also open or select the job.
 */
export function useLongPressCopy(text: string | undefined) {
  const timer = useRef<number | undefined>(undefined);
  const fired = useRef(false);

  const clear = useCallback(() => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (e.button !== 0) return;
      fired.current = false;
      clear();
      timer.current = window.setTimeout(() => {
        timer.current = undefined;
        fired.current = true;
        void copyToClipboard(text ?? '').then((ok) => {
          if (ok) showToast(COPIED_TO_CLIPBOARD, 'info');
        });
      }, LONG_PRESS_MS);
    },
    [clear, text],
  );

  const onClickCapture = useCallback((e: MouseEvent) => {
    if (!fired.current) return;
    fired.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return useMemo(
    () => ({
      onPointerDown,
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerCancel: clear,
      onClickCapture,
    }),
    [onPointerDown, clear, onClickCapture],
  );
}
